/** Parse server dust piece count from assorted API shapes. */
export function parseDustCount(value: unknown): number {
  if (Array.isArray(value)) {
    if (value.length === 1) {
      const inner = parseDustCount(value[0]);
      if (inner > 0) return inner;
    }
    return value.length;
  }
  if (value != null && typeof value === 'object') {
    const o = value as Record<string, unknown>;
    return parseDustCount(o.count ?? o.Count ?? o.amount ?? o.Amount ?? o.pieces ?? o.Pieces ?? o.dust ?? o.Dust);
  }
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
}

export type PendingLoot = { dust: number; item: null | { name: string } };

export function parsePendingDrops(raw: Record<string, unknown> = {}): PendingLoot {
  const pd = (raw.PendingDrops ?? raw.pendingDrops) as Record<string, unknown> | null;
  if (!pd) {
    return {
      dust: parseDustCount(raw.pendingDust ?? raw.PendingDust),
      item: null,
    };
  }
  const itemRaw = (pd.item ?? pd.Item) as Record<string, unknown> | null;
  const itemName = itemRaw ? String(itemRaw.Name ?? itemRaw.name ?? '') : '';
  return {
    dust: parseDustCount(pd.dust ?? pd.Dust ?? pd.pendingDust ?? pd.count ?? pd.Count),
    item: itemName ? { name: itemName } : null,
  };
}

export function parseSessionDrops(raw: Record<string, unknown> = {}) {
  const drops = (raw.drops ?? raw.Drops) as Record<string, unknown> | null;
  if (!drops) return null;
  const dust = parseDustCount(drops.dust ?? drops.Dust);
  const item = (drops.item ?? drops.Item) as Record<string, unknown> | null;
  if (dust <= 0 && !item) return { dust: 0, item: null };
  return { dust, item };
}

/** Resolve how many dust sprites to spawn when a geode breaks. */
export function resolveBreakDustPieces(opts: {
  sessionDust: number;
  pendingDust: number;
  geodeIdChanged: boolean;
  tapsReset: boolean;
  profileDustGrant?: number;
  hasDropsField?: boolean;
}): number {
  const {
    sessionDust,
    pendingDust,
    geodeIdChanged,
    tapsReset,
    profileDustGrant = 0,
    hasDropsField = false,
  } = opts;
  const geodeBroke =
    sessionDust > 0 ||
    geodeIdChanged ||
    tapsReset ||
    (hasDropsField && pendingDust > 0);
  if (!geodeBroke) return 0;

  const lootHint = Math.max(sessionDust, pendingDust);
  // Profile delta is authoritative when the server already applied the grant.
  if (profileDustGrant > 0) return profileDustGrant;
  // Break ack with drops envelope but no profile bump yet — reveal pending loot.
  if (lootHint > 0) return lootHint;
  return 0;
}

export function normalizeGeodesList(data: Record<string, unknown>) {
  const raw = data.geodes ?? data.Geodes ?? data.geode ?? data.Geode;
  if (Array.isArray(raw)) return raw as Record<string, unknown>[];
  if (raw && typeof raw === 'object') return [raw as Record<string, unknown>];
  return [];
}
